import React, { useEffect, useState } from 'react';
import { SdfBox, SdfIcon, SdfLink, SdfButton, SdfAlert, SdfBusyIndicator, SdfFocusPane, SdfTooltip } from '@synerg/react-components';
import axios from 'axios';
import ProjectContext from './projectContext';
import withContext from '../common/withContext';
import get from 'lodash/get';
import { useApplicationState, showErrorModal } from '../../utilities/applicationState';
const HELP_URL = 'https://partnersupport.adp.com/hc/en-us/articles/360036391333-Access-SOR-Help';
const CONSENT_MANAGER_LINK = 'https://adpapps.adp.com/consent-manager/';

function TestInstance({ context }) {
	const [, dispatch] = useApplicationState();
	const { projectDetail: { _id: projectId, b2cConsumerAppID, b2bConsumerAppID, product = {} } } = context;
	// state
	const [title, setTitle] = useState('How to obtain a test instance');
	const [firstStepTitle, setFirstStepTitle] = useState('Request a test instance from ADP');
	const [requestSuccessMessage, setRequestSuccessMessage] = useState('A test instance has been successfully subscribed to your project.');
	const [alreadyRequestedMessage, setAlreadyRequestedMessage] = useState('');
	const [requestButtonMessage, setRequestButtonMessage] = useState('Request a test instance');
	const errorRequestMessage = 'We were unable to complete your request. Please try again, or contact your technical advisor if the issue persists.';
	const [hasSomeoneSubscribedBefore, setHasSomeoneSubscribedBefore] = useState(false);
	const [isLoadingTestInstanceInfo, setLoadingTestInstanceInfo] = useState(false);
	const [isRequestButtonDisabled, disableRequestButton] = useState(true);
	const [errorOnSubscription, setErrorOnSubscription] = useState(false);
	const [, setButtonLoading] = useState(false);
	const [testInstanceData, setTestInstanceData] = useState({ users: [], userNames: [] });
	const [showRequestButton, setShowRequestButton] = useState(false);
	const [isBusy, setBusy] = useState(false);

	const isEndUserOnly = b2cConsumerAppID ? !b2bConsumerAppID : Boolean(b2bConsumerAppID);
	const nextStepsSubtitle = isEndUserOnly
		? 'Leverage your test user credentials from step 2 to test your single sign-on integration.'
		: 'Explore your test instance and continue your integration journey.';
	const nextStepsBody = isEndUserOnly
		? 'Refer to the tab labeled, "End-user/SSO app" within the API credentials section for implementation instructions regarding single sign-on.'
		: 'Leverage the test instance org ID in the sidebar to fetch client credentials that you can use to exchange for an access token, and witness the back and forth movement of data between your prescribed ADP APIs and your test instance.';
	const expectedEmailsAmount = isEndUserOnly ? 'two' : 'three';
	const [modalStatus, setModalStatus] = useState(false);

	// Get test instance data
	useEffect(() => {
		async function getTestInstanceInfo() {
			try {
				setLoadingTestInstanceInfo(true);
				disableRequestButton(true);
				setBusy(true);
				if (projectId !== undefined) {
					const { data = {} } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/${projectId}/test-instance`);
					setShowRequestButton(true);
					const instanceInfo = toTestInstanceInfo(data);
					setTestInstanceData(instanceInfo);
					if (instanceInfo.shouldShowRequestAccessButton) {
						setHasSomeoneSubscribedBefore(true);
						let userName = 'Someone';
						for (let i = 0; i < instanceInfo.userNames.length; i++) {
							if (instanceInfo.userNames[i] === instanceInfo.consentorUsername && instanceInfo.users[i] !== undefined) {
								userName = instanceInfo.users[i];
							}
						}
						switchToRequestAccessState(userName);
					}
				}
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Error occurred when trying to get test instance details. Please try again.');
				dispatch(showErrorModal(errorMessage));
			} finally {
				setLoadingTestInstanceInfo(false);
				setBusy(false);
				if(!testInstanceData.selfSubscribed) disableRequestButton(false);
			}
		}
		getTestInstanceInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectId]);

	const toTestInstanceInfo = (info = {}) => {
		const { shouldShowRequestAccessButton, selfSubscribed, isTestOrgSubscribed, testOrgName, testOrgOID, users, error, userNames, consented, consentorUsername } = info;

		setErrorOnSubscription(error !== '' && error !== undefined);

		return {
			shouldShowRequestAccessButton,
			selfSubscribed: Boolean(selfSubscribed),
			isTestOrgSubscribed: Boolean(isTestOrgSubscribed),
			testOrgName,
			testOrgOID,
			users: Array.isArray(users) ? users : [],
			userNames: Array.isArray(userNames) ? userNames : [],
			consented,
			consentorUsername
		};
	};

	const requestTestInstance = async () => {
		try {
			setErrorOnSubscription(false);
			disableRequestButton(true);
			setButtonLoading(true);
			setBusy(true);
			const { data = {} } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/${projectId}/test-instance/subscribe`, { projectId });
			setTestInstanceData(toTestInstanceInfo(data));
		} catch (error) {
			setErrorOnSubscription(true);
		} finally {
			disableRequestButton(false);
			setButtonLoading(false);
			setRequestSuccessMessage('A test instance has been successfully subscribed to your project. Please check your email.');
			setBusy(false);
		}
	};

	const openTestInstance = async (testProductId) => {
		try {
			disableRequestButton(true);
			setBusy(true);
			const { data: { url } = {} } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/product/${testProductId}/test-instance/url`);
			disableRequestButton(false);
			setBusy(false);
			if (url) {
				window.open(url, '_blank');
			}
		} catch (error) {
			disableRequestButton(false);
			setBusy(false);
		}
	};

	const switchToRequestAccessState = (firstUserToSubscribe) => {
		setTitle('Request access to your test instance');
		setFirstStepTitle('Request access to a test instance from ADP');
		setRequestSuccessMessage('Access to your project\'s test instance has been successfully granted. Please check your email.');
		setAlreadyRequestedMessage(`${firstUserToSubscribe} previously subscribed to an ADP test instance for your project. You may request access to this existing test instance.`);
		setRequestButtonMessage('Request Access');
	};

	const isRequestEnabled = ((testInstanceData.selfSubscribed || isRequestButtonDisabled) && !errorOnSubscription) ? true : undefined;

	const closeModal = () => {
		setModalStatus(false);
	};

	const openModal = () => {
		setModalStatus(true);
	};

	return(
		<div className='sm:w-full md:w-full lg:w-4xl mt-10'>
			<div className='text-heading-07 font-black mb-20'>Test Instance</div>
			<div className='text-heading-06 font-black'>{title}</div>
			<div className='text-heading-03 mb-8'>Follow the steps to request a test instance for testing your integration.</div>
			<SdfBox className='mb-10'>
				<div className='text-heading-03 font-bold mb-2'>What is an ADP test instance?</div>
				<div className='text-sm leading-md'>
					A test instance allows you to test your integration against a fictitious
					organization within an ADP product. This is a shared test instance and changes
					to test data should be expected from time to time.
				</div>
			</SdfBox>

			<div className='step-one mb-16'>
				<div className='text-heading-05 font-bold'>Step 1</div>
				<div className='text-heading-05 font-bold mb-5'>{firstStepTitle}</div>
				<SdfBox className='' variant='bordered'>
					<div className='leading-md mb-10'>
						This production-level test instance allows you to test your integration with ADP.
						By default, you will be assigned the <strong>client practitioner</strong> role which has the highest
						level of access to log in to your test instance.
					</div>
					<div className='flex items-center'>
						{ !showRequestButton && <SdfButton onClick={() => openModal()}><SdfIcon icon="external-link" /> View test instance details</SdfButton> }
						{ showRequestButton && isRequestEnabled && <SdfButton onClick={() => openModal()}><SdfIcon icon="external-link" /> View test instance details</SdfButton> }
						{ showRequestButton  && !isRequestEnabled && <SdfButton disabled={isRequestEnabled} onClick={requestTestInstance}>
							<div style={{height: '1.625rem'}}>
								{requestButtonMessage}
							</div>
						</SdfButton> }
					</div>
					<div className='mt-4 w-5/6'>
						{testInstanceData.selfSubscribed && !errorOnSubscription && <SdfAlert type='banner' status='success' size='sm' icon='alert-success'>{requestSuccessMessage}</SdfAlert>}
						{errorOnSubscription && <SdfAlert type='banner' status='error' size='sm' icon='alert-error'>{errorRequestMessage}</SdfAlert>}
						{!testInstanceData.selfSubscribed && hasSomeoneSubscribedBefore && <SdfAlert type='banner' status='warning' size='sm' icon='alert-warning'>{alreadyRequestedMessage}</SdfAlert>}
					</div>
				</SdfBox>
			</div>
			<div className='step-two mb-16'>
				<div className='text-heading-05 font-bold'>Step 2</div>
				<div className='text-heading-05 font-bold mb-5'>Receive the test instance link and login credentials by email</div>
				<SdfBox className='' variant='bordered'>
					<div className='mb-10'>
						Expect to receive {expectedEmailsAmount} emails from ADP shortly. <br /> <br />
						One email will contain
						your test instance user ID and a link to your test instance, and the
						other email will contain your temporary password.
						{!isEndUserOnly && ' A separate email from ADP Marketplace will be sent related to pending consent.'}
						The Data Connector consent must be allowed by the first test instance user. This can be
						retrieved from the test subscription tab and by clicking on Get subscriber info.
					</div>
					<div>
						<SdfAlert className='w-5/6' type='banner' status='info' size='sm' icon='alert-info'>
							<div className='font-bold leading-sm'>
								These ADP login credentials will be different than your partner credentials
								that were used to sign in to this app. Please keep these stored somewhere safe.
							</div>
						</SdfAlert>
					</div>
					<div className='mt-4'>
						{!isEndUserOnly && <SdfAlert className='w-5/6' type="banner" status='warning' size="sm" icon='alert-warning'>
							<span slot='title'>Be advised</span>
							Before selecting the consent manager link from your consent email, you may need to log out of ADP first to end your current session.
							You will need to log back in to
							<a className="tip-link" target="_blank" rel="noopener noreferrer" href={CONSENT_MANAGER_LINK}> consent manager</a> with your test instance credentials.
							You must provide consent in order to fetch an access token.
						</SdfAlert>}
					</div>
				</SdfBox>
			</div>
			<div className='step-three pb-16'>
				<div className='text-heading-05 font-bold'>Step 3</div>
				<div className='text-heading-05 font-bold mb-5'>Next steps</div>
				<SdfBox className='' variant='bordered'>
					<div className='mb-5'>
						{nextStepsSubtitle} <br /> <br />
						{nextStepsBody}
					</div>
					<SdfLink emphasis="none" href={HELP_URL} target="_blank"> <SdfIcon icon="external-link" /> How to access help for your test instance</SdfLink>
				</SdfBox>
			</div>

			<SdfFocusPane
				visible={modalStatus}
				size='small'
				hideAcceptButton={true}
				hideDismissButton={true}
				onSdfDismiss={() => {
						closeModal();
				}}
				closeable={true}
			>
				<div className='mx-6'>
					<div className='text-heading-06 font-black mb-6'>Test instance details</div>

					<div className='mb-4'>
						<div className='text-heading-04 font-bold'>ADP product test instance</div>
						<SdfLink emphasis='none' href='#!' onClick={() => openTestInstance(get(product,'id',''))}>
							<div className='text-md mt-1'>{get(product, 'name', '')}</div>
						</SdfLink>
					</div>

					<div className='mb-4'>
						<div className='text-heading-04 font-bold'>Test instance org name</div>
						<div className='text-md mt-1'>{get(testInstanceData,'testOrgName','')}</div>
					</div>

					<div className='mb-4'>
						<div className='text-heading-04 font-bold'>Test instance org ID (OOID)</div>
						<div className='text-md mt-1'>{get(testInstanceData,'testOrgOID','')}</div>
					</div>

					<div className='mb-4'>
						<div className='text-heading-04 font-bold mb-1'>
							Test instance user(s)
							<SdfTooltip
								attachmentPoint={'right'}
								positionContext={'ELEMENT'}
							>
							<SdfIcon icon='alert-info' size='sm' slot='tooltip-target' />
							<span>
								The first person in your organization who requests the test
								instance will be the consenter to receive the consent email.
								Please inform the person to consent if hasn’t, following step 2.
							</span>
							</SdfTooltip>
						</div>
						<div className='flex flex-col'>
							{!isLoadingTestInstanceInfo &&
								<>
									{
										!errorOnSubscription &&
										get(testInstanceData, 'userNames', []).length > 0 &&
										testInstanceData.userNames.map((userName, idx) =>
												<div key={`user-data-${idx}`} className={`flex justify-between ${userName === get(testInstanceData, 'consentorUsername', '') ? 'order-1' : 'order-2'}`}>
													<span className='text-md'>{userName}</span>
													{
														userName === get(testInstanceData, 'consentorUsername', '') &&
														<span className={ get(testInstanceData, 'consented', '') ?
																						'uppercase text-sm font-semibold' :
																						'uppercase text-sm font-semibold'}>
															{get(testInstanceData, 'consented', '') ?
																<div style={{color:'#1a871a'}}>
																	<SdfIcon icon='alert-success' title='alert-success' /> Consented
																</div> :
																<div style={{color:'#d40f0f'}}>
																	<SdfIcon icon='alert-warning' title='alert-warning'/> Not Yet Consented
																</div>
															}
														</span>
													}
												</div>
										)
									}
									{
										get(testInstanceData, 'userNames', []).length  === 0 &&
											<span className='text-md' >None</span>
									}
								</>
							}
						</div>
					</div>
					<div>
						<SdfBox>
							<div className='text-heading-04 font-bold'>Reminder</div>
							<div className='text-body-02 mt-1'>
								This is a test environment. Please be mindful not to enter
								any actual client data that may jeopardize a client’s sensitive information.
								If you forget your User ID or password, please use the
								appropriate link on the log in screen for RUN powered by ADP®.
							</div>
						</SdfBox>
					</div>
				</div>
			</SdfFocusPane>
      {isBusy && <SdfBusyIndicator overlay={true} size='lg' fullScreen={true}/>}
		</div>
	)
}

export default withContext(TestInstance, ProjectContext);
